import { FormEvent, useState } from 'react';
import { Alert, Button, CircularProgress, TextField } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import piigLogo from '../../assets/piig-logo.png';
import { getApiErrorMessage } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';

const DEFAULT_BRAND_NAME = 'PILOT INNOVATION GROUP CO.,LTD.';

/** Real login screen — replaces the old no-auth mockup. Posts to POST /auth/login and stores the session in memory only. */
export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const brandName = (import.meta.env.VITE_BRAND_NAME as string | undefined) || DEFAULT_BRAND_NAME;
  const logoUrl = (import.meta.env.VITE_BRAND_LOGO_URL as string | undefined) || piigLogo;

  // Already signed in (e.g. silent refresh succeeded) — don't show the login form.
  if (status === 'authenticated') {
    const redirectTo = (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/plant-view';
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      const redirectTo = (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/plant-view';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Invalid email or password.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[400px] rounded-2xl border border-border bg-surface p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src={logoUrl} alt={brandName} className="h-12 w-12 object-contain" />
          <div>
            <h1 className="font-[var(--font-display)] text-xl font-bold text-text-primary">AquaRefine</h1>
            <p className="text-xs text-text-secondary">Fish-oil refinery control center</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
            disabled={submitting}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            fullWidth
            disabled={submitting}
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-text-secondary">{brandName}</p>
      </div>
    </div>
  );
}
